import { Text } from '@chakra-ui/react';
import { MatchFlames as MatchFlamesMark } from '@fluenceur/agent-blocks';
import type { ApplicationMatchFlames } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';

/** The tier each lit count stands for; its i18n key (`applications.match.<tier>`) is what the screen reader and the hover say. */
const TIER = { 3: 'excellent', 2: 'good', 1: 'moderate' } as const;

/** 17px in lists and tiles, 22px on a creator sheet. */
const SIZE = { list: 'sm', sheet: 'md' } as const;

interface MatchFlamesProps {
  flames: ApplicationMatchFlames;
  size?: keyof typeof SIZE;
}

/**
 * The brand-match score read as flames, never as a number: three slots always
 * drawn, the lit ones orange, with the same mark as every other creator
 * surface (`@fluenceur/agent-blocks`). The tier ("Bon match, 2/3") is what a
 * hover and a screen reader get. Below the proposal floor reads as quiet "off
 * target" text without any flame; an unknown score shows nothing at all,
 * because a creator nobody scored is not off target.
 */
export function MatchFlames({ flames, size = 'list' }: MatchFlamesProps) {
  const { t } = useTranslation();

  if (flames === null) return null;

  if (flames === 0) {
    return (
      <Text as="span" fontSize="12px" color="inkSubtle">
        {t('applications.match.offTarget')}
      </Text>
    );
  }

  const label = t(`applications.match.${TIER[flames]}` as const);

  return (
    <MatchFlamesMark
      tier={TIER[flames]}
      size={SIZE[size]}
      ariaLabel={label}
      title={label}
      gap="1px"
    />
  );
}
